import React from 'react';
import { useTranslation } from 'react-i18next';
import { AlertTriangle, X, Eye, Calendar, Building, MapPin } from 'lucide-react';
import type { JobInfo } from '../utils/jobExtractor';

interface DuplicateJobWarningProps {
  jobInfo: JobInfo;
  onProceed: () => void;
  onCancel: () => void;
}

const DuplicateJobWarning: React.FC<DuplicateJobWarningProps> = ({
  jobInfo,
  onProceed,
  onCancel
}) => {
  const { t } = useTranslation();
  return (
    <div className="bg-gradient-to-r from-orange-50 to-red-50 border-2 border-orange-300 rounded-xl p-6 mb-6 animate-slideIn">
      <div className="flex items-start justify-between">
        <div className="flex items-start space-x-4">
          <div className="flex-shrink-0">
            <div className="flex items-center justify-center w-12 h-12 bg-orange-100 rounded-full">
              <AlertTriangle className="w-6 h-6 text-orange-600" />
            </div>
          </div>
          
          <div className="flex-1">
            <h3 className="text-lg font-semibold text-orange-900 mb-2">
              ⚠️ {t('duplicate_job.title')}
            </h3>
            
            <p className="text-orange-800 mb-4">
              {t('duplicate_job.description')}
            </p>
            
            <div className="bg-white bg-opacity-60 rounded-lg p-4 mb-4">
              <h4 className="font-medium text-gray-900 mb-2">{t('duplicate_job.details_title')}</h4>
              <div className="space-y-2 text-sm">
                <div className="flex items-center text-gray-700">
                  <Eye className="w-4 h-4 mr-2 text-blue-500" />
                  <span className="font-medium">{t('duplicate_job.position')} :</span>
                  <span className="ml-2">{jobInfo.title}</span>
                </div>
                <div className="flex items-center text-gray-700">
                  <Building className="w-4 h-4 mr-2 text-green-500" />
                  <span className="font-medium">{t('duplicate_job.company')} :</span>
                  <span className="ml-2">{jobInfo.company}</span>
                </div>
                {jobInfo.location && (
                  <div className="flex items-center text-gray-700">
                    <MapPin className="w-4 h-4 mr-2 text-purple-500" />
                    <span className="font-medium">{t('duplicate_job.location')} :</span>
                    <span className="ml-2">{jobInfo.location}</span>
                  </div>
                )}
                <div className="flex items-center text-gray-700">
                  <Calendar className="w-4 h-4 mr-2 text-orange-500" />
                  <span className="font-medium">{t('duplicate_job.first_analysis')} :</span>
                  <span className="ml-2">{jobInfo.date}</span>
                </div>
              </div>
            </div>
            
            <div className="flex items-center space-x-3">
              <button
                onClick={onProceed}
                className="bg-orange-600 hover:bg-orange-700 text-white px-4 py-2 rounded-lg font-medium transition-colors flex items-center"
              >
                <Eye className="w-4 h-4 mr-2" />
                {t('duplicate_job.proceed')}
              </button>
              
              <button
                onClick={onCancel}
                className="bg-gray-100 hover:bg-gray-200 text-gray-700 px-4 py-2 rounded-lg font-medium transition-colors"
              >
                {t('duplicate_job.cancel')}
              </button>
            </div>
          </div>
        </div>
        
        <button
          onClick={onCancel}
          className="flex-shrink-0 p-2 hover:bg-orange-100 rounded-lg transition-colors"
        >
          <X className="w-5 h-5 text-orange-600" />
        </button>
      </div>
      
      <div className="mt-4 p-3 bg-blue-50 rounded-lg border border-blue-200">
        <div className="flex items-start">
          <div className="text-blue-500 mr-2 mt-0.5">
            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
            </svg>
          </div>
          <div className="text-sm text-blue-800">
            <p className="font-medium">💡 {t('duplicate_job.why_detected_title')}</p>
            <p className="mt-1">
              {t('duplicate_job.why_detected_desc')}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DuplicateJobWarning;